import { columnsBanners } from '@/domain/env/colums-banners';
import { DOCUMENTS_DB } from '@/domain/env/documents-db';
import { Banner } from '@/domain/types/collections';
import usePaginatedDocuments from '@/domain/use-cases/get-paginated-documents';
import Button from '@/presentation/components/button';
import LoadMoreButton from '@/presentation/components/load-more-button';
import Table from '@/presentation/components/table';
import { useState } from 'react';
import { FiPlus } from 'react-icons/fi';
import { MdCheckCircle, MdFilterList } from 'react-icons/md';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

const BannersPage = () => {
  const navigate = useNavigate();
  const { data, error, isLoading, isLoadingMore, hasMore, loadMore } = usePaginatedDocuments<Banner>(
    DOCUMENTS_DB.BANNERS,
  );

  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all');

  if (isLoading) return <div>Loading...</div>;

  if (error) return <div>Error: {error}</div>;

  if (!data.length && !isLoading) return <div>No hay datos</div>;

  // Filtrar banners según el filtro seleccionado
  const filteredData = data.filter((banner) => {
    if (filter === 'active') return banner.isActive;
    if (filter === 'inactive') return !banner.isActive;
    return true;
  });

  // Contar banners activos
  const activeBannersCount = data.filter((b) => b.isActive).length;

  const handleRowClick = (row: Banner) => {
    navigate(`/dashboard/banners/details/${row.id}`);
  };

  return (
    <div className="p-4 flex flex-col gap-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Banners</h1>
          <p className="text-sm text-gray-600 mt-1">
            Gestiona las publicaciones destacadas del sistema
          </p>
        </div>

        <RouterLink to="/dashboard/banners/add">
          <Button label="Crear banner" icon={<FiPlus />} onClick={() => {}} />
        </RouterLink>
      </div>

      {/* Info de banners activos */}
      <div
        className={`p-4 rounded-lg border-2 ${
          activeBannersCount >= 3
            ? 'bg-yellow-50 border-yellow-300'
            : 'bg-blue-50 border-blue-300'
        }`}
      >
        <div className="flex items-center gap-3">
          <MdCheckCircle
            className={
              activeBannersCount >= 3 ? 'text-yellow-500' : 'text-blue-500'
            }
            size={24}
          />
          <div>
            <h3
              className={`font-semibold ${
                activeBannersCount >= 3 ? 'text-yellow-800' : 'text-blue-800'
              }`}
            >
              {activeBannersCount} de 3 banners activos
            </h3>
            <p
              className={`text-sm ${
                activeBannersCount >= 3 ? 'text-yellow-700' : 'text-blue-700'
              }`}
            >
              {activeBannersCount >= 3
                ? 'Límite alcanzado. Al activar uno nuevo, se desactivará automáticamente el más antiguo.'
                : `Puedes activar ${3 - activeBannersCount} banner(es) más.`}
            </p>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex items-center gap-2">
        <MdFilterList className="text-gray-600" size={20} />
        <span className="text-sm font-medium text-gray-700">Filtrar:</span>

        <button
          onClick={() => setFilter('all')}
          className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
            filter === 'all'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Todos ({data.length})
        </button>

        <button
          onClick={() => setFilter('active')}
          className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
            filter === 'active'
              ? 'bg-green-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Activos ({activeBannersCount})
        </button>

        <button
          onClick={() => setFilter('inactive')}
          className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
            filter === 'inactive'
              ? 'bg-gray-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Inactivos ({data.length - activeBannersCount})
        </button>
      </div>

      {/* Tabla */}
      <Table
        data={filteredData}
        columns={columnsBanners}
        onRowClick={handleRowClick}
      />

      <LoadMoreButton hasMore={hasMore} isLoadingMore={isLoadingMore} onLoadMore={loadMore} />
    </div>
  );
};

export default BannersPage;
